import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser, ClientSelfView } from "@themislex/shared";
import { api, setAuthFailureHandler } from "../api/client";
import { derivePasswordProof } from "@themislex/shared";
import { useTheme } from "../theme/ThemeContext";

interface MeResponse {
  user: AuthUser;
  client?: {
    profile: ClientSelfView["profile"];
    matterCount: number;
    openInvoiceCount: number;
    balanceMinor: number;
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  clientSelf: ClientSelfView | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  completeSetup: (token: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [clientSelf, setClientSelf] = useState<ClientSelfView | null>(null);
  const [loading, setLoading] = useState(true);
  const { authMode } = useTheme();

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "same-origin" });
      if (!res.ok) {
        setUser(null);
        setClientSelf(null);
        return;
      }
      const body = (await res.json()) as MeResponse;
      setUser(body.user);
      if (body.user.role === "client" && body.client) {
        setClientSelf({
          profile: body.client.profile,
          matterCount: body.client.matterCount,
          openInvoiceCount: body.client.openInvoiceCount,
          balanceMinor: body.client.balanceMinor,
        });
      } else {
        setClientSelf(null);
      }
    } catch {
      setUser(null);
      setClientSelf(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me", { credentials: "same-origin" })
      .then(async (res) => {
        if (!alive) return;
        if (!res.ok) {
          setUser(null);
          setClientSelf(null);
          setLoading(false);
          return;
        }
        const body = (await res.json()) as MeResponse;
        if (!alive) return;
        setUser(body.user);
        if (body.user.role === "client" && body.client) {
          setClientSelf({
            profile: body.client.profile,
            matterCount: body.client.matterCount,
            openInvoiceCount: body.client.openInvoiceCount,
            balanceMinor: body.client.balanceMinor,
          });
        } else {
          setClientSelf(null);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setUser(null);
        setClientSelf(null);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setAuthFailureHandler(() => {
      setUser(null);
      setClientSelf(null);
    });
    return () => setAuthFailureHandler(null);
  }, []);

  /**
   * In relief mode the browser derives the bcrypt proof locally (the Worker's
   * 10ms free-tier CPU budget cannot afford server-side bcrypt); in server
   * mode the raw password travels over TLS to the Worker instead.
   */
  const buildCredentials = useCallback(
    async (password: string, email: string) =>
      authMode === "relief"
        ? { passwordProof: await derivePasswordProof(password, email) }
        : { password },
    [authMode],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const credentials = await buildCredentials(password, email);
      const body = await api<{ user: AuthUser }>("/api/auth/login", {
        method: "POST",
        json: { email, ...credentials },
      });
      setUser(body.user);
      await refreshUser();
      return body.user;
    },
    [buildCredentials, refreshUser],
  );

  const completeSetup = useCallback(
    async (token: string, password: string) => {
      const res = await fetch("/api/auth/me", { credentials: "same-origin" });
      const me = res.ok ? ((await res.json()) as MeResponse) : null;
      const email = me?.user.email ?? "";
      const credentials = await buildCredentials(password, email);
      const body = await api<{ user: AuthUser }>("/api/auth/setup", {
        method: "POST",
        json: { token, ...credentials },
      });
      setUser(body.user);
      await refreshUser();
      return body.user;
    },
    [buildCredentials, refreshUser],
  );

  const logout = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      /* clearing local state matters more than the call succeeding */
    }
    setUser(null);
    setClientSelf(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, clientSelf, loading, login, completeSetup, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
